import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Entities } from '../../data-rules/data-rules-model';
import { MetaDataService } from '../../data-rules/meta-data.service';

export interface IFieldMappingsConfig {
  mappings: Record<string, string>;
  strategies: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class EnrichmentFieldMappingsService {

  constructor(
    private http: HttpClient,
    private metaDataService: MetaDataService
  ) {}

  /**
   * Returns writable Candidate fields from the already-cached /meta response.
   * ADDRESS fields are expanded into dot-notation sub-fields (e.g. address.city).
   * Excludes read-only, TO_ONE, and TO_MANY fields.
   */
  getAvailableFields(): Observable<{ fieldName: string; fieldLabel: string }[]> {
    return this.metaDataService.getAllMeta().pipe(
      mergeMap(() => this.metaDataService.getActionMeta('Candidate' as Entities)),
      map(fields => {
        const result: { fieldName: string; fieldLabel: string }[] = [];

        fields
          .filter(f => !f.readOnly)
          .filter(f => f.fieldName !== 'id')
          .filter(f => f.metaType !== 'TO_MANY' && f.metaType !== 'TO_ONE')
          .forEach(f => {
            if (f.metaType === 'ADDRESS' && f.addressFields?.length) {
              // Expand address into individual sub-fields with dot-notation
              f.addressFields.forEach(af => result.push({
                fieldName: `${f.fieldName}.${af.fieldName}`,
                fieldLabel: `${f.label} - ${af.label}`
              }));
            } else {
              result.push({
                fieldName: f.fieldName,
                fieldLabel: f.label || f.fieldName
              });
            }
          });

        return result;
      })
    );
  }

  getFieldMappings(): Observable<IFieldMappingsConfig> {
    return this.http.get<IFieldMappingsConfig>(`${environment.dataRulesUrl}/enrichment/getFieldMappings`);
  }

  saveFieldMappings(mappings: Record<string, string>, strategies: Record<string, string>): Observable<any> {
    const safeMappings = Object.fromEntries(Object.entries(mappings).filter(([k]) => k !== ''));
    const safeStrategies = Object.fromEntries(Object.entries(strategies).filter(([k]) => k !== ''));
    return this.http.post(`${environment.dataRulesUrl}/enrichment/saveFieldMappings`, { mappings: safeMappings, strategies: safeStrategies });
  }
}
